import {ValidationRule} from "@vuelidate/core";
import {Ref, ref, UnwrapRef} from "@vue/composition-api";
import {AxiosResponse} from "axios";
import * as Sentry from '@sentry/browser';

interface Data {
    [key: string]: any
}

export interface FormErrorResponse {
    message: string,
    // errors?: FormValidationErrors<TData>
    errors?: {
        [key: string]: string[]
    }
}

export type FormValidationErrors<TData extends Data> = {
    [key in keyof TData]?: string[]
};

type Attributes<TData extends Data> = {
    [key in keyof TData]?: string
};

interface ValidationArgs {
    [K: string]: ValidationRule | ValidationArgs
}

type SubmitResponse<TResponse> = Promise<{ success?: TResponse, error?: TResponse }>;

/**
 * Was thinking about removing data and just use it as
 */
export type useFormHelperInterface<TData extends Data> = Ref<{
    data: TData;
    attributes: Attributes<TData> | UnwrapRef<Attributes<TData>>;
    error: UnwrapRef<FormErrorResponse | null>;
    processing: boolean;
    reset: (...fields: string[]) => void;
    submit: <TResponse>(callback: (data: TData) => Promise<AxiosResponse<TResponse>>) => SubmitResponse<TResponse>,
}>;

interface useAPiFormSettings {
    // Keep loading because it is waiting for redirect => new state
    keepProcessingOnSuccess: boolean
}

export default function useFormHelper<TData extends Data>(
    inputData: TData, attributes: Attributes<TData> = {}, settings: useAPiFormSettings = {
        keepProcessingOnSuccess: true
    }
): useFormHelperInterface<TData> {

    const initialData = JSON.parse(JSON.stringify(inputData))

    const data: Ref<TData> = ref(Object.assign({}, initialData));

    let error = ref<FormErrorResponse | null>(null);

    const processing = ref(false);

    const reset = (...fields: string[]) => {
        const replaceFields = fields.length > 0 ? fields : Object.keys(initialData);
        for (let i = 0; i < replaceFields.length; i++) {
            // @ts-ignore
            data.value[replaceFields[i]] = initialData[replaceFields[i]];
        }
    }

    const submit: <TResponse>(callback: (data: TData) => Promise<AxiosResponse<TResponse>>) => SubmitResponse<TResponse> =
        async (callback) => {

            error.value = null;
            processing.value = true;

            let showAlert = true;

            try {
                const response = await callback(data.value);

                if (!settings.keepProcessingOnSuccess) {
                    processing.value = false;
                }

                return {
                    success: response.data
                }
            } catch (e) {
                let logError = true;

                if (e.response) {
                    if (e.response.data && e.response.data.message) {
                        error.value = {
                            message: e.response.data.message,
                            errors: e.response.data.errors
                        }
                        showAlert = false;
                        logError = false;
                    }

                    if (e.response.status) {
                        if (e.response.status === 419) {
                            alert('Page expired. Please refresh the page and try again');
                            window.location.reload();
                            showAlert = false;
                            logError = false;
                        }
                    }
                }

                if (logError) {
                    Sentry.captureException(e);
                }

                if (showAlert) {
                    alert('Oops! Something went wrong. Our engineers have been notified of this problem and will work to resolve it.');
                }

                processing.value = false;

                return {
                    error: e
                }
            }
        }

    return ref({
        data,
        attributes,
        // errors: errors as unknown as FormValidationErrors<TData>,
        error: error,
        processing,
        reset,
        submit
    });
}


